import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Calendar, User } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../auth/AuthContext";

const NAV_LINKS = [
  { name: "Home", to: "/" },
  { name: "Veteran Services", to: "/veteran-services" },
  { name: "Insurance", to: "/insurance" },
  { name: "Education", to: "/education" },
  { name: "Resources", to: "/resources" },
  { name: "About", to: "/about" },
  { name: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-sm shadow-sm border-b border-slate-100"
          : "bg-white/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <div className="bg-slate-900 p-2 rounded-sm">
              <img
                src="/logo.png"
                alt="Noble Strategies Consulting"
                className="h-10 md:h-12 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.name}
                to={link.to}
                data-testid={`nav-${link.name.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors ${
                    isActive
                      ? "text-slate-900 border-b-2 border-[#C89B3C] pb-1"
                      : "text-slate-600 hover:text-slate-900"
                  }`
                }
                end={link.to === "/"}
              >
                {link.name}
              </NavLink>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {user && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
              >
                <User size={14} /> Admin
              </Link>
            )}
            <Link
              to="/schedule"
              data-testid="nav-schedule"
              className="bg-slate-900 text-white px-5 py-3 text-sm font-medium tracking-wide hover:bg-[#0B2343] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              <Calendar size={16} />
              Schedule Consultation
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden bg-white border-t border-slate-100 py-3"
          >
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.name}
                to={link.to}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `block py-3 px-4 font-medium ${
                    isActive
                      ? "text-slate-900 bg-slate-50 border-l-4 border-[#C89B3C]"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                end={link.to === "/"}
              >
                {link.name}
              </NavLink>
            ))}
            {user && (
              <NavLink
                to="/admin"
                data-testid="mobile-nav-admin"
                className="block py-3 px-4 text-slate-700 hover:bg-slate-50 font-medium"
              >
                Admin Dashboard
              </NavLink>
            )}
            <div className="px-4 pt-3 pb-1">
              <Link
                to="/schedule"
                data-testid="mobile-cta-schedule"
                className="block w-full text-center bg-slate-900 text-white px-6 py-3 font-medium"
              >
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
